import type { AudiobookDecryptedFileMap, AudiobookEncryptedFileMap, AudiobookEncryptedFileMapEntry } from '../types/audiobooks';
// eslint-disable-next-line import/no-cycle
import { decryptPassword } from '../utils/deviceCrypto.platform';
// eslint-disable-next-line import/no-cycle
import { forceReRegister } from '../utils/deviceKeyRegistration.platform';
import exceptionHandler from '../utils/exceptionHandler.platform';
// eslint-disable-next-line import/no-cycle
import { clearDeviceEncryptedContentCaches } from './methods';

const DECRYPT_FAILED = Symbol('decrypt-failed');

type DecryptResult = string | null | typeof DECRYPT_FAILED;

export interface DecryptAudiobookFileMapOptions {
  profileId: number;
  logContext?: {
    docId?: string;
    parsedDocId?: string;
  };
}

function formatErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

async function decryptEntryPassword(
  internalId: string,
  entry: AudiobookEncryptedFileMapEntry,
  options: DecryptAudiobookFileMapOptions,
): Promise<DecryptResult> {
  if (!entry.ep) {
    return entry.password ?? null;
  }

  try {
    return await decryptPassword(entry.ep, options.profileId);
  } catch (error) {
    exceptionHandler.captureException('RSA decryption of audiobook password failed, attempting recovery', {
      extra: {
        internalId,
        profileId: options.profileId,
        profileIdType: typeof options.profileId,
        error: formatErrorMessage(error),
        ...options.logContext,
      },
    });
    return DECRYPT_FAILED;
  }
}

async function recoverAndRetryDecryption(
  failedIds: string[],
  options: DecryptAudiobookFileMapOptions,
): Promise<void> {
  try {
    await clearDeviceEncryptedContentCaches();
    await forceReRegister(options.profileId);
  } catch (error) {
    exceptionHandler.captureException('RSA recovery failed while re-registering device key', {
      extra: {
        profileId: options.profileId,
        profileIdType: typeof options.profileId,
        error: formatErrorMessage(error),
        failedIds,
        ...options.logContext,
      },
    });
  }
}

export async function decryptAudiobookFileMap(
  encryptedFileMap: AudiobookEncryptedFileMap | null | undefined,
  options: DecryptAudiobookFileMapOptions,
): Promise<AudiobookDecryptedFileMap | undefined> {
  if (!encryptedFileMap) {
    return undefined;
  }

  const result: AudiobookDecryptedFileMap = {};
  const failedIds: string[] = [];

  for (const [internalId, entry] of Object.entries(encryptedFileMap)) {
    const decryptedPassword = await decryptEntryPassword(internalId, entry, options);
    if (decryptedPassword === DECRYPT_FAILED) {
      failedIds.push(internalId);
    }
    result[internalId] = {
      data_file_url: entry.data_file_url,
      password: decryptedPassword === DECRYPT_FAILED ? null : decryptedPassword,
      ep: entry.ep,
    };
  }

  if (failedIds.length === 0) {
    return result;
  }

  await recoverAndRetryDecryption(failedIds, options);
  return result;
}
